import { useState } from "react"

import { faIcon } from "@utils/faIcon"
import replaceSlugs from "@utils/replaceSlugs"

import { Avatar, IconButton } from "@atoms/index"

import AlertBadge from "@molecules/AlertBadge/AlertBadge"
import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"
import { Prompt } from "@molecules/index"

import { MenuContentNode } from "@organisms/Menu/Menu.types"
import { Menu } from "@organisms/index"

import {
    useSubscribeMutation,
    useSubscriptionPreviewCountMutation,
    useUnsubscribeFromBrandMutation,
} from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.store"

import { CATALOG_ROUTES } from "@routes/catalogs"

import styles from "./CatalogBrandRow.module.scss"
import { CatalogBrandRowProps } from "./CatalogBrandRow.types"

function getSubscriptionCaption(subscribedItemCount: number, itemCount: number): string {
    const itemNoun = itemCount === 1 ? "item" : "items"

    if (subscribedItemCount === 0) {
        return `${itemCount} ${itemNoun}`
    }

    return `${subscribedItemCount} of ${itemCount} ${itemNoun} subscribed`
}

export default function CatalogBrandRow(props: CatalogBrandRowProps) {
    const { catalogId, brand, count, isFullySubscribed } = props

    const subscribedItemCount = count?.subscribed_item_count ?? 0
    const itemCount = count?.item_count ?? 0

    const previewCountMutation = useSubscriptionPreviewCountMutation()
    const subscribeMutation = useSubscribeMutation()
    const unsubscribeMutation = useUnsubscribeFromBrandMutation()

    const [isSubscribePromptOpen, setIsSubscribePromptOpen] = useState(false)
    const [isUnsubscribePromptOpen, setIsUnsubscribePromptOpen] = useState(false)

    const isNoBrand = brand.id === null
    const displayName = isNoBrand ? "(No brand)" : brand.name

    const brandItemsRoute = isNoBrand
        ? replaceSlugs(CATALOG_ROUTES.NO_BRAND_ITEMS, { id: String(catalogId) })
        : replaceSlugs(CATALOG_ROUTES.BRAND_ITEMS, { id: String(brand.id) })

    // A null brand omits the brand key, subscribing to the catalog's "No Brand" bucket.
    const subscribePayload = { catalog: catalogId, level: "brand" as const, brand: brand.id ?? undefined }

    const handleSubscribeClick = () => {
        setIsSubscribePromptOpen(true)
        previewCountMutation.mutate(subscribePayload)
    }

    const handleSubscribeConfirm = () => {
        setIsSubscribePromptOpen(false)
        subscribeMutation.mutate(subscribePayload)
    }

    const handleUnsubscribeConfirm = () => {
        setIsUnsubscribePromptOpen(false)
        unsubscribeMutation.mutate({ catalogId, brand: { id: brand.id, name: displayName } })
    }

    const isActionPending = subscribeMutation.isPending || unsubscribeMutation.isPending

    const menuItems: MenuContentNode[] = [
        {
            type: "group",
            items: [
                {
                    label: "View items",
                    icon: faIcon({ name: "arrow-up-right-from-square", style: "regular" }),
                    onClick: () => window.location.assign(brandItemsRoute),
                },
                ...(!isFullySubscribed
                    ? [
                          {
                              label: isNoBrand ? "Subscribe" : "Subscribe to brand",
                              icon: faIcon({ name: "circle-plus", style: "regular" as const }),
                              onClick: handleSubscribeClick,
                          },
                      ]
                    : []),
            ],
        },
        ...(isFullySubscribed
            ? [
                  { type: "divider" as const },
                  {
                      type: "group" as const,
                      items: [
                          {
                              variant: "danger" as const,
                              label: isNoBrand ? "Unsubscribe" : "Unsubscribe from brand",
                              icon: faIcon({ name: "ban", style: "regular" as const }),
                              onClick: () => setIsUnsubscribePromptOpen(true),
                          },
                      ],
                  },
              ]
            : []),
    ]

    const previewCount = previewCountMutation.data?.count
    const ongoingImportClause = isNoBrand
        ? "new items added without a brand later will be imported automatically too"
        : "new items added to this brand later will be imported automatically too"
    const subscribeBody =
        previewCount === undefined
            ? isNoBrand
                ? `This will import items without a brand into your pricebook and keep them in sync — ${ongoingImportClause}. Are you sure you want to do this?`
                : `This will import this brand's items into your pricebook and keep them in sync — ${ongoingImportClause}. Are you sure you want to do this?`
            : `This will import ${previewCount} ${previewCount === 1 ? "item" : "items"} into your pricebook and keep them in sync — ${ongoingImportClause}. Are you sure you want to do this?`

    // The wrapper shields the menu from the row-level click handler, so opening the
    // menu (or picking one of its items) never doubles as a row navigation.
    const stopRowClick = (event: React.MouseEvent | React.KeyboardEvent) => {
        event.stopPropagation()
    }

    return (
        <>
            <ListItem.Root
                isClickable={true}
                onClick={() => window.location.assign(brandItemsRoute)}
                aria-label={`View ${displayName} items`}
            >
                <ListItem.AvatarAndContent
                    avatar={
                        isNoBrand ? (
                            // Reserve the avatar slot so the label column lines up with sibling rows.
                            <div className={styles.avatarSpacer} aria-hidden={true} />
                        ) : (
                            <Avatar
                                variant="company"
                                size="lg"
                                src={brand.image_url || undefined}
                                fallbackName={brand.name}
                            />
                        )
                    }
                    content={
                        <ListItemContent
                            leftLabel={
                                <ListItem.Label
                                    label={displayName}
                                    suffixSlot={
                                        isFullySubscribed ? (
                                            <AlertBadge status="success" size="sm" showIcon={true} isLoading={false}>
                                                Subscribed
                                            </AlertBadge>
                                        ) : undefined
                                    }
                                />
                            }
                            leftCaption={
                                <ListItem.Caption caption={getSubscriptionCaption(subscribedItemCount, itemCount)} />
                            }
                        />
                    }
                />
                <ListItem.Actions>
                    <div
                        className={styles.menuGuard}
                        onClick={stopRowClick}
                        onKeyDown={stopRowClick}
                        role="presentation"
                    >
                        <Menu
                            popoverSide="bottom"
                            popoverAlign="end"
                            title={displayName}
                            trigger={
                                <IconButton
                                    variant="ghost"
                                    size="md"
                                    icon={faIcon({ name: "ellipsis", style: "regular" })}
                                    aria-label="More options"
                                    isProcessing={isActionPending}
                                    isDisabled={isActionPending}
                                />
                            }
                            items={menuItems}
                        />
                    </div>
                </ListItem.Actions>
            </ListItem.Root>

            <Prompt
                title={isNoBrand ? "Subscribe to items without a brand?" : `Subscribe to ${brand.name}?`}
                body={subscribeBody}
                isOpen={isSubscribePromptOpen}
                confirmText="Subscribe"
                onConfirm={handleSubscribeConfirm}
                cancelText="Cancel"
                onCancel={() => setIsSubscribePromptOpen(false)}
            />

            <Prompt
                title={isNoBrand ? "Unsubscribe from items without a brand?" : `Unsubscribe from ${brand.name}?`}
                body="Items already in your pricebook will stay there, but they will no longer receive catalog updates, and new items in this brand will no longer be imported automatically."
                isOpen={isUnsubscribePromptOpen}
                confirmText="Unsubscribe"
                onConfirm={handleUnsubscribeConfirm}
                cancelText="Cancel"
                onCancel={() => setIsUnsubscribePromptOpen(false)}
                isDangerousOperation={true}
            />
        </>
    )
}
